import React, { Component, useRef, useState, useEffect } from 'react';
import {Link, useNavigate, useParams } from "react-router-dom";
import NavBarMini from './NavBarMini.js';
import Footer from './Footer.js';
import LoadingAnimation from './LoadingAnimation.js';
import * as utils from '../common/Utils.js'
import { useAdminGuard } from '../hooks/useAdminGuard.js';

import { collection, query, where, getDoc, getDocs, setDoc, updateDoc, doc, Timestamp, serverTimestamp } from "firebase/firestore";
import { db } from '../firebase.js';

const DATABASE_SOURCE = utils.getDatabaseSource();

let DATE_WHEN_BLOCKS_AND_STEALS_WERE_ADDED = new Date('May 27, 2024');
let SAVE_BLOCKS_AND_STEALS = true;

const AdminSeasonTeams = () => {
  const navigate = useNavigate();
  const { isReady } = useAdminGuard();
  const { teamID, season } = useParams();
  const [players, setPlayers] = useState([])
  const [dataReady, setDataReady] = useState(false);
  const [team, setTeam] = useState({})
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredPlayers, setFilteredPlayers] = useState([]);
  const [teamPlayers, setTeamPlayers] = useState([]);
  const [otherTeamPlayers, setOtherTeamPlayers] = useState([]);
  const [allPlayersInSeason, setAllPlayersInSeason] = useState([]);
  const [currentSeason, setCurrentSeason] = useState({
    conferences: [],
    leaders: [],
    teams: [],
    upcomingGames: []
  })
  const [selectedConference, setSelectedConference] = useState('');
  const [games, setGames] = useState([]);
  const [teams, setTeams] = useState([]);
  const [saveStatus, setSaveStatus] = useState(null); // 'success' | 'error' | null

  useEffect(() => {
    if (!isReady) return;

    const load = async () => {
      await getCurrentSeasonData();
      const players = await getPlayers();
      await getTeams(players);
      await getGames();
      setDataReady(true);
    };
    load();
  }, [isReady]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (players.length > 0) {
      getAllSeasonPlayers().then(players => {
        setAllPlayersInSeason(players);
      });
    }
  }, [players]);

  useEffect(() => {
    if (allPlayersInSeason.length === 0) {
      return;
    }
    const filtered = searchTerm.trim() === ''
      ? [] // Show no results if searchTerm is blank
      : allPlayersInSeason.filter(player =>
          `${player.firstName} ${player.lastName}`.toLowerCase().includes(searchTerm.toLowerCase())
        );

    setFilteredPlayers(filtered);
  }, [searchTerm, allPlayersInSeason]);

  async function getPlayers () {
    const playerSnapshot = await getDocs(collection(db, 'players'));
    const players = playerSnapshot.docs.map((doc) => doc.data());

    setPlayers(players)
    return players
  }

  const getTeams = async (players) => {
    const teamsRef = collection(db, "teams");
    const teamsSnapshot = await getDocs(teamsRef);
    const teamsData = teamsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));

    const seasonTeamsRef = collection(db, "seasonTeams");
    const seasonTeamsSnapshot = await getDocs(seasonTeamsRef);
    const seasonTeamsData = seasonTeamsSnapshot.docs.map(doc => ({
      seasonTeamID: doc.id,
      ...doc.data(),
    }));

    const teams = seasonTeamsData.filter(st => {
      return st.season == season // loose equality handles string/number mismatch in Firestore
    }).map(st => {
      const team = teamsData.find(t => t.id === st.teamID);
      return { ...st, id: st.teamID, ...team };
    })
    setTeams(teams);

    const currentSeasonTeam = teams.find((team) => team.id == teamID)
    let tmPlyrs = []
    if (currentSeasonTeam) {
      tmPlyrs = currentSeasonTeam.players.map((playerID) => utils.getPlayerByID(players, playerID))
      
      setTeam(currentSeasonTeam)
      setSelectedConference(currentSeasonTeam.conference)
    }
    setTeamPlayers(tmPlyrs)

    const otherSeasonTeams = teams.filter((team) => team.id !== teamID)
    let otherPlayers = otherSeasonTeams.map((team) => team.players.map((playerID) => utils.getPlayerByID(players, playerID)))
    setOtherTeamPlayers([].concat(...otherPlayers))
  }

  const getGames = async () => {
    const gamesRef = collection(db, "games");
    const q = query(gamesRef, where("season", "==", parseInt(season)));
    const gamesSnapshot = await getDocs(q);

    let gamesData = gamesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));

    let games = gamesData.map(game => ({
      ...game,
      date: game.date.toDate()
    }));
    games.sort((a, b) => a.date - b.date);

    setGames(games);
  };

  const getAllSeasonPlayers = async () => {
    const q = query(
      collection(db, "signups"),
      where("season", "==", season),
      where("paid", "==", true)
    );
    const signupsSnapshot = await getDocs(q);

    const signups = signupsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    const currentTeamPlayerIDs = teamPlayers.map(player => player?.id || '');
    const otherTeamPlayersIDs = otherTeamPlayers.map(player => player?.id || '');

    const paidPlayers = signups
      .map(signup => utils.getPlayerByID(players, signup.player_id))
      .filter(Boolean);

    const playersNotOnTeam = paidPlayers.filter(player =>
      !currentTeamPlayerIDs.includes(player.id)
    );

    const finalList = playersNotOnTeam.filter(player =>
      !otherTeamPlayersIDs.includes(player.id)
    );

    return finalList;
  };

  const handleSelect = (conference) => {
    setSelectedConference(conference);
  };

  const getCurrentSeasonData = async () => {
    const seasonRef = doc(db, "seasons", season);
    const seasonSnapshot = await getDoc(seasonRef);
    const seasonData = seasonSnapshot.data();

    setCurrentSeason(seasonData);
  }

  function addPlayer (playerID) {
    const player = utils.getPlayerByID(players, playerID)
    let plyrs = teamPlayers
    plyrs.push(player)
    setTeamPlayers(plyrs)
    setSearchTerm('')
  }

  function removePlayer(playerID) {
    const updatedTeamPlayers = teamPlayers.filter(player => player.id !== playerID);

    setTeamPlayers(updatedTeamPlayers);
  }

  function displayEditSeasonTeam () {
    return (
      <>
        {saveStatus === 'success' && (
          <div className="alert alert-success" role="alert">
            Team saved successfully.
          </div>
        )}
        {saveStatus === 'error' && (
          <div className="alert alert-danger" role="alert">
            Failed to save team. Please try again.
          </div>
        )}
        <form>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="inputTeamName">Team Name</label>
              <input type="text" className="form-control" id="inputTeamName" defaultValue={team.name} required disabled />
            </div>
            <div className="form-group">
              <label htmlFor="inputTeamNameAbbv">Team Name Abbv.</label>
              <input type="text" className="form-control" id="inputTeamNameAbbv" defaultValue={team.nameAbbv} required disabled />
            </div>
          </div>
          <div className="form-row">
            {currentSeason.conferences?.length > 0 ? (
              <div className="form-group">
                <label htmlFor="inputTeamConference">Conference</label>
                <button
                  className="btn btn-secondary dropdown-toggle"
                  type="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  { selectedConference }
                </button>
                <ul className="dropdown-menu">
                  {currentSeason.conferences.map((conference, index) => (
                    <button
                      key={index}
                      type="button"
                      className="dropdown-item"
                      onClick={() => handleSelect(conference)}
                    >
                      {conference}
                    </button>
                  ))}
                </ul>
              </div>
            ) : (
              <></>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="inputPlayerSearch">Search for player</label>
            <input type="text" className="form-control" id="playerSearch"
              placeholder="Player name"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}>
            </input>
          </div>
          {searchTerm.trim() === '' ? ( <></>
            ) : (
            filteredPlayers.length > 0 ? (
              <div className='returning-player-search-results'>
                {filteredPlayers.map((player, index) => (
                  <div className='returning-player-signup'>
                    <p key={index}>
                      {player.firstName} {player.lastName}
                    </p>
                      <button className="btn btn-success" type="button" onClick={() => addPlayer(player.id)}>+ Add Player</button>
                  </div>
                ))}
              </div>
            ) : (
              <p>No players found</p>
                )
            )}
          <div className="form-group">
            <label>Players</label>
            <ul className="list-group">
              { teamPlayers.map((player, index) => {

                if (!player) {
                  player = {
                    firstName: 'unknown',
                    lastName: 'unknown',
                    id: 'unknown'
                  }
                }

                return (
                  <div className='league-admin-season-team'>
                    <input key={index} className="form-control" value={ utils.displayPlayerName(player) } readOnly></input>
                    <button
                      className="btn btn-danger"
                      type="button"
                      onClick={() => removePlayer(player.id)}
                    >
                      Delete
                    </button>
                  </div>
                )
              })}
            </ul>
          </div>
          <button className="btn btn-dark" type="button" onClick={saveTeam}>Save Team</button>
        </form>
      </>
    )
  }

  async function saveTeam () {
    try {
      const seasonTeamID = utils.seasonTeamID(team.id, season)
      await setDoc(doc(db, "seasonTeams", seasonTeamID), {
        players: teamPlayers.map(player => player.id),
        // Firestore rejects undefined; seasons without conferences store ''.
        conference: selectedConference || ''
      }, { merge: true });

      // Recompute season standings with the updated conference applied in memory,
      // since the teams state still holds the old value at this point.
      if (teams.length > 0) {
        const teamsWithUpdatedConference = teams.map(t =>
          t.id === team.id ? { ...t, conference: selectedConference || '' } : t
        );
        const seasonRef = doc(db, 'seasons', season);
        const updatedSeasonTeams = utils.getSeasonTeams(games, teamsWithUpdatedConference, season);
        await setDoc(seasonRef, { teams: updatedSeasonTeams, updatedAt: serverTimestamp() }, { merge: true });
      }

      setSaveStatus('success');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setSaveStatus('error');
    }
  }

  function createTeam () {
    navigate('/admin/teams/new');
  }

  if (dataReady == false || !team || team == {}) {
    return (
      <div className="Schedule">
        <LoadingAnimation />
      </div>
    )
  }
  else {
    return (
      <div className="Schedule">
        <div className="league-content">
          <div className="league-body">
            <p className='league-admin-back-button'><a className='league-link' href="/admin"><b>← Back to Admin Home</b></a></p>
            { teamID ? <p className='league-admin-back-button'><a className='league-link' href={"/admin/seasons/" + season}><b>← Back to Season { season }</b></a></p> : '' }

            <p className="league-title-standard-secondary text-align-center">Edit Team - St. {team.name}</p>
            { teamID ? '' : <a type="button" className="btn btn-dark" onClick={createTeam}>+ Create Team</a> }
            <div className="league-table-container schedule large-item-buttons-admin">
              { displayEditSeasonTeam() }
            </div>
          </div>
        </div>

        <Footer/>
      </div>
    );
  }
}

export default AdminSeasonTeams;
